import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { addAdministrativeCharge } from '../../servicesHooks/Honorarium/useHonorariumLiquidationService';
import { parseApiError } from '../../services/apiError';
import useNotify from '../../hooks/useNotify';
import FieldError from '../../components/FieldError/FieldError';

type FieldName = 'description' | 'amount';
type FieldErrors = Partial<Record<FieldName, string>>;

/** Campo del request → campo del formulario, para los marcadores de VALIDATION_ERROR. */
const REQUEST_FIELDS: Record<string, FieldName> = { description: 'description', amount: 'amount' };

/** Largo de la columna de la descripción del gasto. */
const DESCRIPTION_MAX_LENGTH = 1000;

interface AddAdministrativeChargeDialogProps {
    visible: boolean;
    liquidationProfessionalId: number;
    onHide: () => void;
    onAdded: () => void;
}

/**
 * Gasto administrativo a cargo del profesional (consultorio, secretaria, servicios de la clínica); se descuenta del
 * neto al cerrar la liquidación. Se carga a mano: los conceptos facturables son de pacientes y financiadores.
 */
export default function AddAdministrativeChargeDialog({
    visible,
    liquidationProfessionalId,
    onHide,
    onAdded,
}: AddAdministrativeChargeDialogProps) {
    const notify = useNotify();

    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState<number | null>(null);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [saving, setSaving] = useState(false);
    const descriptionRef = useRef<HTMLInputElement>(null);
    const amountRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!visible) return;
        setDescription('');
        setAmount(null);
        setErrors({});
    }, [visible]);

    function validate(): FieldErrors {
        const result: FieldErrors = {};
        if (!description.trim()) result.description = 'Ingresá la descripción';
        if (amount == null || amount <= 0) result.amount = 'Ingresá un monto mayor a cero';
        return result;
    }

    function focusFirstError(fieldErrors: FieldErrors) {
        if (fieldErrors.description) descriptionRef.current?.focus();
        else if (fieldErrors.amount) amountRef.current?.focus();
    }

    async function handleSave() {
        const validationErrors = validate();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            focusFirstError(validationErrors);
            return;
        }

        setSaving(true);
        try {
            await addAdministrativeCharge({
                liquidationProfessionalId,
                amount: amount!,
                description: description.trim(),
            });
            notify.success('Gasto administrativo agregado');
            onAdded();
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            const fieldErrors: FieldErrors = {};
            for (const detail of apiError.details ?? []) {
                const field = REQUEST_FIELDS[detail.field];
                if (field) fieldErrors[field] = detail.message;
            }
            if (Object.keys(fieldErrors).length > 0) {
                setErrors(fieldErrors);
                focusFirstError(fieldErrors);
                return;
            }
            const isBusinessError = apiError.status >= 400 && apiError.status < 500;
            notify.error(isBusinessError ? apiError.message : 'No se pudo agregar el gasto administrativo. Intentá de nuevo.');
        } finally {
            setSaving(false);
        }
    }

    const footer = (
        <div className="md-dialog__footer">
            <Button outlined label="Cancelar" className="md-ghost" onClick={onHide} disabled={saving} />
            <Button label="Agregar gasto" className="md-primary" onClick={handleSave} loading={saving} />
        </div>
    );

    return (
        <Dialog
            header="Agregar gasto administrativo"
            visible={visible}
            onHide={() => { if (!saving) onHide(); }}
            footer={footer}
            style={{ width: '560px' }}
            breakpoints={{ '640px': '100vw' }}
            className="md-dialog"
            modal
        >
            <div className="liquidation-form">
                <div className="inputContainer flexible--column">
                    <label htmlFor="admin-charge-description" className="label inputLabel">Descripción *</label>
                    <InputText
                        id="admin-charge-description"
                        ref={descriptionRef}
                        value={description}
                        onChange={(e) => {
                            setDescription(e.target.value);
                            setErrors(current => ({ ...current, description: undefined }));
                        }}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        placeholder="Ej.: alquiler de consultorio"
                        className={errors.description ? 'field-error-target' : undefined}
                        aria-invalid={errors.description ? true : undefined}
                        aria-describedby={errors.description ? 'admin-charge-description-error' : undefined}
                    />
                    <FieldError id="admin-charge-description-error" message={errors.description} />
                </div>
                <div className="inputContainer flexible--column">
                    <label htmlFor="admin-charge-amount" className="label inputLabel">Monto *</label>
                    <InputNumber
                        inputId="admin-charge-amount"
                        inputRef={amountRef}
                        value={amount}
                        onValueChange={(e) => {
                            setAmount(e.value ?? null);
                            setErrors(current => ({ ...current, amount: undefined }));
                        }}
                        mode="currency"
                        currency="ARS"
                        locale="es-AR"
                        minFractionDigits={2}
                        min={0}
                        className={errors.amount ? 'field-error-target' : undefined}
                        aria-invalid={errors.amount ? true : undefined}
                        aria-describedby={errors.amount ? 'admin-charge-amount-error' : undefined}
                    />
                    <FieldError id="admin-charge-amount-error" message={errors.amount} />
                </div>
            </div>
        </Dialog>
    );
}
